// The onboarding funnel, stripped to a blank slate.
//
// There is deliberately no design here. No colours, no type scale, no spacing,
// no motion, no keyboard affordances, no progress bar, no transcript, no value
// lines, no stat cards. Every element is the plainest tag that says what it is,
// and `.onboarding-plain` in index.css puts the browser's own defaults back
// (Tailwind's preflight having removed them), so what renders is what an HTML
// document renders.
//
// This exists so each slide can be redesigned from nothing. The rule while it
// lasts: if a change would make a slide look better, it does not belong in this
// file yet. The only things a slide should carry are the question it asks, the
// input that answers it, and where the lead is in the sequence.

export function OnboardingShell({ children }: { children: React.ReactNode }) {
  return <div className="onboarding-plain">{children}</div>;
}

// "Step 4 of 18". The one piece of chrome kept, because a slide with no sense
// of position is not a slide you can judge — you cannot tell whether it is
// asking too much this early or too little this late.
export function StepCounter({
  index,
  total,
}: {
  index: number;
  total: number;
}) {
  return (
    <p>
      Step {index + 1} of {total}
    </p>
  );
}

export function Question({
  title,
  subtitle,
}: {
  title: string;
  subtitle?: string | null;
}) {
  return (
    <>
      <h1>{title}</h1>
      {subtitle && <p>{subtitle}</p>}
    </>
  );
}

export function Field({
  label,
  value,
  onChange,
  placeholder,
  inputType,
  onEnter,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string | null;
  inputType: string;
  onEnter?: () => void;
}) {
  return (
    <p>
      <label>
        {label}
        <br />
        {inputType === "textarea" ? (
          <textarea
            value={value}
            rows={3}
            placeholder={placeholder ?? undefined}
            onChange={(e) => onChange(e.target.value)}
          />
        ) : (
          <input
            type={inputType}
            value={value}
            placeholder={placeholder ?? undefined}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && onEnter) {
                e.preventDefault();
                onEnter();
              }
            }}
          />
        )}
      </label>
    </p>
  );
}

// One option in a list. A radio or a checkbox, which is what these questions
// have always been underneath.
export function Option({
  name,
  label,
  multi,
  checked,
  onChange,
}: {
  name: string;
  label: string;
  multi: boolean;
  checked: boolean;
  onChange: () => void;
}) {
  return (
    <li>
      <label>
        <input
          type={multi ? "checkbox" : "radio"}
          name={name}
          checked={checked}
          onChange={onChange}
        />{" "}
        {label}
      </label>
    </li>
  );
}

export function Button({
  children,
  onClick,
  disabled,
}: {
  children: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
}) {
  return (
    <button type="button" onClick={onClick} disabled={disabled}>
      {children}
    </button>
  );
}

// Anything the funnel needs to say back: an error, a confirmation, a note about
// state. One tag, no tone — which of these need to be distinguishable, and how,
// is a redesign question rather than a settled one.
export function Message({ children }: { children: React.ReactNode }) {
  return <p>{children}</p>;
}

export function ExpiredLink() {
  return (
    <>
      <h1>This link has expired</h1>
      <p>
        Sign-in links are single-use and last 30 minutes. Nothing you filled in
        is lost — entering the same work email picks it up where you left off.
      </p>
      <p>
        <a href="/onboarding">Start again</a>
      </p>
    </>
  );
}
